import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteCategoryItem,
  getCategoriesList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateCategoriesBunch,
} from "store/gameCenter/categories/slice";
import { gameCenterLimits } from "configs/limits";
import { ModalTypes, GameCenterFormTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { usePermissionHelper } from "helpers/usePermissionHelper";

import ToggleBtn from "components/buttons/ToggleBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import ActionBtn from "components/buttons/ActionBtn";
import Pagination from "components/Pagination";
import GameCenterFilters from "../GameCenterFilters";

import editIcon from "assets/icons/edit-icon.svg";
import eyeIcon from "assets/icons/eye.svg";
import eyeOffIcon from "assets/icons/eye-off.svg";
import listIcon from "assets/icons/list-icon.svg";
import gridIcon from "assets/icons/grid-icon.svg";

import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import { useGuideModal } from "modals/guides/useGuideModal";
import { GuideButton } from "modals/guides/GuideButton";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import ImageWithFallback from "helpers/ImageWithFallback";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const CategoriesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { openGuide } = useGuideModal();
  const activeCategoryId = searchParams.get("entityId");

  const [order, setOrder] = useState<{
    title: string;
    slug: string;
    countOfCollections: string;
  }>({
    title: "none",
    slug: "none",
    countOfCollections: "none",
  });
  const [activeView, setView] = useState<string>("table");
  const [currentLimit, setLimit] = useState<number>(gameCenterLimits[2]);
  const [search, setSearch] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);

  const permissions = usePermissionHelper();

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const categoriesList = useAppSelector((store) => store.categories.list);
  const isListFetched = useAppSelector(
    (store) => store.categories.isListFetched,
  );
  const totalItems = useAppSelector((store) => store.categories.totalItems);
  const totalPages = useAppSelector((store) => store.categories.totalPages);
  const activePage = useAppSelector((store) => store.categories.activePage);
  const selectedItems = useAppSelector(
    (store) => store.categories.selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store.categories.processingItemId,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'categories',
    isListFetched
  });

  useEffect(() => {
    if (permissions && !permissions.read) {
      navigate("/dashboard");
    }
    const grid = localStorage.getItem("grid");
    if (grid) {
      setView(grid);
    }
  }, [navigate, permissions]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getCategoriesList({
          limit: currentLimit,
          offset: activePage - 1,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          title: search,
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };
  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };
  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleOpenCreateModal = () => {
    if (permissions?.create) {
      navigate(
        `${location.pathname}?activeModal=${ModalTypes.gameCenter}&gModalType=${GameCenterFormTypes.category}`,
      );
      dispatch(openModal({ activeModal: ModalTypes.gameCenter }));
    }
  };
  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.gameCenter}&gModalType=${GameCenterFormTypes.category}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.gameCenter }));
  };
  const handleDeleteItem = async (id: number) => {
    if (!permissions?.delete) return;

    const resultCategory = await dispatch(deleteCategoryItem(id ?? 0));
    if (resultCategory?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleActionClick = async (e: string) => {
    if (!permissions?.update) return;

    try {
      await dispatch(
        updateCategoriesBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <GameCenterFilters
        searchValue={search}
        handleSearch={handleFilterSearch}
        selectedActive={active}
        handleUpdateActive={(e) => setActive(e)}
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <>
          <div className="page-list-header">
            {permissions?.create && (
              <div className="page-list-header--box">
                <ActionBtn
                  txt={t("addCategory")}
                  handleClick={handleOpenCreateModal}
                />
              </div>
            )}
            <div className="page-list-header--box page-list-header--box--right">
              <RefreshButton
                position="right"
                disabled={isRefreshRequested}
                isLoading={isRefreshRequested}
                onClick={() => refresh()}
              />
            </div>
            <div className="page-list-header--box page-list-header--box-actions">
              <ToggleBtn
                handleClick={(v) => {
                  localStorage.setItem("grid", v);
                  setView(v);
                }}
                activeValue={activeView}
                disabled={categoriesList.length === 0}
                icons={[
                  {
                    url: listIcon,
                    value: "table",
                  },
                  {
                    url: gridIcon,
                    value: "grid",
                  },
                ]}
              />
              <GuideButton onClick={() => openGuide("gamecentre")} />
              {permissions?.update && (
                <DefaultDropdown
                  onSelect={(e) => {
                    handleActionClick(e as string);
                  }}
                  placeholder={t("chooseAction")}
                  options={options}
                  isSmall
                  disabled={selectedItems.length === 0}
                />
              )}
            </div>
          </div>

          {!isListFetched && (
            <>
              <DataTableSkeleton columnsCount={8} rowsCount={15} />
              <PaginationSkeleton />
            </>
          )}

          {isListFetched && (
            <>
              <div
                className={`game-table-wrap ${activeView === "table" ? "" : "grid"}`}
              >
                {categoriesList.length === 0 && (
                  <EmptyState message={t("noCategoriesFound")} />
                )}

                {categoriesList.length > 0 && (
                  <>
                    {activeView === "table" ? (
                      <Table
                        data={categoriesList}
                        idField="id"
                        columns={{
                          sortingColumns: {
                            countOfCollections: {
                              value: t("collections"),
                              label: "cnt",
                              tooltip: t("info"),
                              hasSorting: true,
                            },
                          },
                          hiddenColumns: ['icon']
                        }}
                        order={order}
                        selectedItems={selectedItems}
                        onAllSelect={handleAllClick}
                        onSelect={(id) => handleItemClick(id)}
                        onDelete={(id) => handleDeleteItem(id)}
                        onEdit={(id) => handleOpenEditModal(id)}
                        onView={(id) => handleOpenEditModal(id)}
                        hasEdit={permissions?.update}
                        hasView={!permissions?.update}
                        hasDelete={permissions?.delete}
                        hasCheckbox={permissions?.update}
                        activeRowId={activeCategoryId}
                        onSort={(id) => (id ? handleOrderClick(id) : {})}
                        processingId={processingItemId}
                      />
                    ) : (
                      <div className="game-grid">
                        {categoriesList.map((el) => (
                          <div className="game-item big" key={el.id}>
                            <div className="game-item-img">
                              <ImageWithFallback
                                src={el.image}
                                alt={el.title}
                                className="game-item-img__image"
                              />
                            </div>
                            <div className="game-item-content">
                              <div className="game-item-content--title">
                                {el.title}
                              </div>
                              <div className="game-item-content--actions">
                                {permissions?.update && (
                                  <div
                                    onClick={() => handleItemClick(el.id)}
                                    className={`def-check-control ${
                                      selectedItems.includes(el.id)
                                        ? "checked"
                                        : ""
                                    }`}
                                  />
                                )}
                                <div className="provider">
                                  {el.countOfCollections}
                                </div>
                                <div
                                  className="option"
                                  onClick={() => handleOpenEditModal(el.id)}
                                >
                                  <img
                                    src={
                                      permissions?.update
                                        ? editIcon
                                        : String(activeCategoryId) === String(el.id)
                                          ? eyeOffIcon
                                          : eyeIcon
                                    }
                                    alt={permissions?.update ? "Edit Icon" : "View Icon"}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {categoriesList.length > 0 && (
                <Pagination
                  handleLimitClick={(e) => {
                    setLimit(e);
                    dispatch(handlePaginate(1));
                  }}
                  limitsArray={gameCenterLimits}
                  activePage={activePage}
                  limit={currentLimit}
                  totalItems={totalItems}
                  totalPages={totalPages}
                  handleNextClick={handleNextClick}
                  handlePrevClick={handlePrevClick}
                />
              )}
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default CategoriesList;
